import axios from 'axios';
import { call, delay, put, select } from 'redux-saga/effects';

import * as AppActions from '../../../../actions';
import * as ListenerUtils from '../../../../lib/ListenerUtils';
import { putAndWait } from '../../../../sagas/TaskManager';
import ApplicationConstants from '../../constants';
import * as D from '../../deps';
import { isAdBlockerDetected as getIsAdBlockerDetected } from '../../utils/Application';

const spawn = D.taskManagerSpawnFactory(ApplicationConstants.DOMAIN_NAME);

// Utility to create bait elements for adblocker detection
export function createBaitElement() {
  const bait = document.createElement('div');
  bait.setAttribute(
    'class',
    'ad_placeholder ad_hidden invisible_ad ad_slot promo_content promo-banner ad-container sponsored_link',
  );
  bait.setAttribute(
    'style',
    'width: 1px !important; height: 1px !important; position: absolute !important; left: -10000px !important; top: -1000px !important;',
  );
  return bait;
}

// Checks if an element is blocked
export function isElementBlocked(el) {
  const style = window.getComputedStyle(el, null);
  return el.offsetParent === null || style.display === 'none' || style.visibility === 'hidden';
}

// Detect adblocker using bait elements
export function detectedAdblockerThroughBait() {
  const bait = createBaitElement();
  document.body.appendChild(bait);
  const detected = isElementBlocked(bait);
  document.body.removeChild(bait);
  return detected;
}

// Detect adblocker using script loading
export function detectedAdblockerThroughScriptLoading() {
  const src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
  const iframe = document.createElement('iframe');
  iframe.setAttribute('id', 'ad-blocker-detector');

  return new Promise(resolve => {
    const html = `
      <body style='margin: 0; overflow: hidden;'>
        <script async src="${src}" onload="notifyParent(false)" onerror="notifyParent(true)"></script>
        <script>
          let iframeRef;
          function load(iframe) {
            iframeRef = iframe;
          }
          function notifyParent(detected) {
            const event = new CustomEvent('adBlockerDetected', { detail: detected });
            iframeRef.dispatchEvent(event);
          }
        </script>
      </body>`;
    document.body.appendChild(iframe);
    iframe.contentWindow.document.write(html);
    iframe.contentWindow.load(iframe);

    iframe.addEventListener('adBlockerDetected', ({ detail }) => {
      iframe.remove();
      resolve(detail);
    });
  });
}

// Detect adblocker using sbar.json check
export function* detectedAdblockerThroughSbarCheck() {
  try {
    const assetLocation =
      ASSET_LOCATION === '/web-client/' ? ASSET_LOCATION : `/web-version/${__VERSION__}/`;
    const url = `${assetLocation}sbar.json?key=${Date.now()}`;
    const timeout = D.AppConstants.NETWORK_PULSE_TIMEOUT_MS;
    const response = yield call(axios.get, url, { timeout, responseType: 'json' });
    const { data } = response;
    return data.version !== __VERSION__;
  } catch (error) {
    if (error.code === D.ServiceErrorConstants.CONNECTION_ABORTED) return false;
    return true;
  }
}

// Main adblocker detection logic
export function* isAdBlockerDetected() {
  const detectedThroughScriptLoading = yield call(detectedAdblockerThroughScriptLoading);
  const detectedThroughSbarCheck = yield call(detectedAdblockerThroughSbarCheck);
  const detectedThroughAdsJs = yield call(detectedAdBlockerThroughAdsJs);
  const detectedThroughBait = yield call(detectedAdblockerThroughBait);

  return (
    [
      detectedThroughScriptLoading,
      detectedThroughSbarCheck,
      detectedThroughAdsJs,
      detectedThroughBait,
    ].filter(Boolean).length >= 2
  );
}

// Checks if ads.js element is blocked
export function detectedAdBlockerThroughAdsJs() {
  const element = document.getElementById('NZDlnkaIbCOH_PANDORA');
  return !element;
}

// AdBlocker modal handling
export function* openAdBlockerModal() {
  const { dispatch } = D.getStore();
  const modalConfig = {
    showMeHow: () =>
      dispatch(
        D.getInDomain('analytics.actions.logAdBlockerHammerModalEvent')({
          clicked: true,
          reason: 'clicked_cta',
        }),
      ),
    closeModal: () =>
      dispatch(D.AppActions.closeAdBlockerModal()) &&
      dispatch(
        D.getInDomain('analytics.actions.logAdBlockerHammerModalEvent')({
          clicked: true,
          reason: 'dismissed',
        }),
      ),
  };
  yield put(
    D.getInDomain('analytics.actions.logAdBlockerHammerModalEvent')({
      clicked: false,
    }),
  );
  yield put(D.ModalActions.openAdBlockerModal(modalConfig));
}

export function* closeAdBlockerModal() {
  yield put(D.ModalActions.closeModal());
}

// Adblocker monitor loop
export function* adBlockerMonitor(source) {
  const interval = yield select(D.TunerVarUtils.getAdBlockCheckInterval);
  const useInterval = interval && source === 'app_start';

  do {
    const wasBlockedBefore = yield select(getIsAdBlockerDetected);
    const isBlocked = yield call(isAdBlockerDetected);
    yield put(D.AppActions.setIsAdBlockerDetected(isBlocked, source));
    const isFreeUser = yield select(ListenerUtils.shouldShowDisplayAds);
    const isDesktop = D.ClientUtils.isElectron();
    if (isFreeUser && !isDesktop) yield spawn(handleAdBlocker, isBlocked, wasBlockedBefore);
    if (useInterval) yield delay(interval);
  } while (useInterval);
}

// Handles changes in adblocker status
export function* handleAdBlocker(isBlocked, wasBlockedBefore) {
  if (isBlocked !== wasBlockedBefore) {
    yield put(D.AppActions.setIsAntiAdBlockerHammerMode(isBlocked));
    yield call(initiateHammerAdBlockHandler);
  }
}

// Initiates anti-adblock actions
export function* initiateHammerAdBlockHandler() {
  const isHammerMode = yield select(
    D.getInDomain('application.utils.isAntiAdBlockerHammerModeDetected'),
  );
  const isAudioInitialized = yield select(D.getInDomain('playback.utils.isInitialized'));
  if (isHammerMode && isAudioInitialized && !isAdBlockerModalVisible()) {
    yield call(openAdBlockerModal);
    yield spawn(pauseAudioAndPlayAntiAdBlockSample);
  }
}

// Plays anti-adblocker sample audio
export function* pauseAudioAndPlayAntiAdBlockSample() {
  const antiAdBlockerData = D.getInDomain('listener.utils.getAntiAdBlockerEducationAudioUrl')();
  const isPlaying = yield select(D.getInDomain('playback.utils.isAudioPlaying'));
  if (isPlaying) {
    yield call(putAndWait, AppActions.UseCases.pauseTrack({ logPlaybackActionsAnalytics: false }));
    yield put(
      D.getInDomain('analytics.actions.logPlaybackInteractions')({
        action: 'ad_block_pause',
        userInitiated: false,
      }),
    );
  }
  yield put(D.getInDomain('analytics.actions.logAdBlockerAudioMessageCoachmark')());
  yield call(
    putAndWait,
    AppActions.SamplePlayer.playSample(antiAdBlockerData.url, antiAdBlockerData.resumeTrackOnEnd),
  );
}

function isAdBlockerModalVisible() {
  return document.getElementsByClassName('Modal__container__body--adBlocker').length > 0;
}

// Adblocker watcher saga
export function* detectAdBlockerWatcher({ payload }) {
  yield call(adBlockerMonitor, payload.source);
}
